import {DependencyList, RefObject, useCallback, useLayoutEffect, useMemo} from 'react';

import {useVariableHeightChangeReporter} from './useVariableHeightChangeReporter';

/*
 * Hook.
 */

/**
 * Reports the height of an element over time.
 *
 * The element is measured when:
 * - after a render when dependencies changed (if dependencies are given).
 * - after every render (if no dependencies are given).
 * - when the returned function is called.
 */
export function useVariableHeightElement(
  elementRef: RefObject<HTMLElement>,
  dependencies?: DependencyList,
  onHeightChange?: (arg: number) => void,
): () => void {
  const reportHeight = useVariableHeightChangeReporter();

  const reportElementHeight = useCallback(() => {
    const element = elementRef.current;
    if (!element) {
      reportHeight(undefined);
      return;
    }

    const {height} = element.getBoundingClientRect();
    reportHeight(height);
    if (onHeightChange) {
      onHeightChange(height);
    }
  }, [elementRef, onHeightChange, reportHeight]);

  // If dependencies are given, add reportElementHeight to those dependencies.
  const extendedDependencies = useMemo(
    () => (dependencies ? [reportElementHeight, ...dependencies] : undefined),
    [reportElementHeight, dependencies],
  );

  useLayoutEffect(
    () => {
      reportElementHeight();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    extendedDependencies,
  );

  return reportElementHeight;
}
